'use client';

import { addSeconds } from 'date-fns';

import {
  getCachedShuffledOptions,
  getOrCreateShuffledOptions,
  useProjectResponseModeContext,
} from '@/shared-survey';
import {
  GetPollResultQuery,
  GetProjectQuery,
  InitiateSurveyV3Mutation,
  MessageSender,
  MessageV2,
  OptionRenderType,
  V2GetInsightQuery,
} from '@/survey-graphql';

import { OPTIMISTIC_MESSAGE_TEMP_REPLY_ID } from './project-response-chat.helpers';
import {
  NO_MATRIX_FIELDS,
  resolveMessageMatrixFields,
} from './project-response-matrix-question.helpers';
import { useProjectResponseTTS } from './project-response-tts.hooks';
import { NIL_UUID, NIL_UUID_2 } from './project-response.consts';
import {
  enumStringEquals,
  getOrCreateInitialMessageCreatedAt,
  getQuestionType,
  POLL_MESSAGE_LOADING_UUID,
  POLL_MESSAGE_UUID,
} from './project-response.helpers';
import {
  resolveLegacyUrlQuestionText,
  resolveLegacyUrlQuestionTranscript,
} from './survey-project-response-url-question-text.helpers';

interface UseProjectResponseMessagesProps {
  sessionId: string;
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
  insightData: V2GetInsightQuery | undefined;
  project: GetProjectQuery['project'];
  pollResultData: GetPollResultQuery | undefined;
  isPollResultLoading: boolean;
  speechAudios: ReturnType<typeof useProjectResponseTTS>['speechAudios'];
}

export function useInitialMessageFromProject(
  sessionId: string,
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'],
  projectLanguage: string | null | undefined
) {
  const initialMessageFromProject: MessageV2 = (() => {
    const optionsToUse = firstQuestion?.options?.length
      ? getOrCreateShuffledOptions(
          sessionId,
          firstQuestion.id,
          firstQuestion.options,
          firstQuestion.randomize ?? false
        )
      : undefined;
    return {
      id: NIL_UUID,
      text: resolveLegacyUrlQuestionText(firstQuestion.text, projectLanguage),
      content: '',
      createdAt: new Date().toISOString(),
      sender: MessageSender.Assistant,
      optionRenderType: firstQuestion.optionRenderType,
      assets: firstQuestion.assets,
      transcript:
        resolveLegacyUrlQuestionTranscript(
          firstQuestion.text,
          firstQuestion.textForVoice,
          projectLanguage
        ) ?? firstQuestion.text,
      messageAssets: null,
      contentUrl: firstQuestion.contentUrl,
      mobileAppEmbedUrl: firstQuestion.mobileAppEmbedUrl ?? null,
      mobileAppFileName: firstQuestion.mobileAppFileName ?? null,
      metadata: {
        type: getQuestionType(firstQuestion?.type),
        options: optionsToUse ?? [],
        isConversationOver: false,
        assistantQuestion: false,
        questionId: firstQuestion.id,
        required: firstQuestion.required,
        maxSelections: firstQuestion.maxSelections,
      },
    };
  })();
  return initialMessageFromProject;
}

export function useProjectResponseMessages({
  sessionId,
  firstQuestion,
  insightData,
  project,
  pollResultData,
  isPollResultLoading,
  speechAudios,
}: UseProjectResponseMessagesProps) {
  const { responseMode } = useProjectResponseModeContext();

  const pollResultOptions = (() => {
    if (!pollResultData?.pollResults || !project.questions[0]?.options) {
      return null;
    }
    return pollResultData.pollResults.distribution.map((r) => ({
      id: project.questions[0].options?.find((o) => o.value === r.value)?.id,
      label:
        project.questions[0].options?.find((o) => o.value === r.value)?.label ??
        '',
      value: r.value,
      count: r.count,
    }));
  })();

  const totalResponses = pollResultData?.pollResults?.totalResponses;

  const initialMessageFromProject: MessageV2 = (() => {
    const optionsToUse = firstQuestion?.options?.length
      ? getOrCreateShuffledOptions(
          sessionId,
          firstQuestion.id,
          firstQuestion.options,
          firstQuestion.randomize ?? false
        )
      : undefined;
    return {
      id: NIL_UUID,
      text: resolveLegacyUrlQuestionText(firstQuestion.text, project.language),
      content: '',
      createdAt: getOrCreateInitialMessageCreatedAt(sessionId),
      sender: MessageSender.Assistant,
      optionRenderType: firstQuestion.optionRenderType,
      assets: firstQuestion.assets,
      transcript:
        resolveLegacyUrlQuestionTranscript(
          firstQuestion.text,
          firstQuestion.textForVoice,
          project.language
        ) ?? firstQuestion.text,
      messageAssets: null,
      contentUrl: firstQuestion.contentUrl,
      mobileAppEmbedUrl: firstQuestion.mobileAppEmbedUrl ?? null,
      mobileAppFileName: firstQuestion.mobileAppFileName ?? null,
      metadata: {
        type: getQuestionType(firstQuestion?.type),
        options: optionsToUse ?? [],
        isConversationOver: false,
        assistantQuestion: false,
        questionId: firstQuestion.id,
        required: firstQuestion.required,
        maxSelections: firstQuestion.maxSelections,
      },
    };
  })();

  const unsortedMessages = (() => {
    let insightMessages = insightData?.insightV2.messages ?? [
      initialMessageFromProject,
    ];

    const introText = project.introText;
    if (
      introText &&
      !insightMessages.some((m) => m.createdAt === new Date(0).toISOString())
    ) {
      insightMessages = [
        {
          id: NIL_UUID_2,
          text: introText,
          createdAt: new Date(0).toISOString(),
          sender: MessageSender.Assistant,
          optionRenderType: OptionRenderType.Text,
        },
        ...insightMessages,
      ];
    }

    const cachedOptionsForFirstQuestion =
      firstQuestion.randomize &&
      getCachedShuffledOptions(sessionId, firstQuestion.id);

    const messages = insightMessages.map((m) => {
      const questionId = m.metadata?.questionId ?? null;
      const useCachedOrder =
        cachedOptionsForFirstQuestion && questionId === firstQuestion.id;
      const options = useCachedOrder
        ? cachedOptionsForFirstQuestion
        : (m.metadata?.options ?? undefined);
      const assistantQuestion = m.metadata?.assistantQuestion ?? false;
      const { subQuestions, notApplicableLabel } = resolveMessageMatrixFields(
        project.questions,
        { sender: m.sender, questionId, assistantQuestion }
      );
      return {
        id: m.id,
        text: resolveLegacyUrlQuestionText(m.text, project.language),
        createdAt: m.createdAt,
        sender: m.sender,
        type: m.metadata?.type,
        options,
        optionRenderType: m.optionRenderType ?? OptionRenderType.Text,
        assets: m.assets,
        transcript: resolveLegacyUrlQuestionTranscript(
          m.text,
          m.transcript,
          project.language
        ),
        maxSelections: m.metadata?.maxSelections ?? null,
        contentUrl: m.contentUrl,
        questionId,
        assistantQuestion,
        subQuestions,
        notApplicableLabel,
      };
    });
    return messages;
  })();

  const messages = (() => {
    let sortedMessages = unsortedMessages.toSorted(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    sortedMessages = sortedMessages.filter((m) => m.text !== '');

    const firstUserResponseIndex = sortedMessages.findIndex(
      (m) => m.sender === MessageSender.User
    );

    // Handle poll result insertion/update
    if (firstUserResponseIndex !== -1) {
      // Check if poll message already exists
      const existingPollIndex = sortedMessages.findIndex(
        (m) => m.id === POLL_MESSAGE_UUID || m.id === POLL_MESSAGE_LOADING_UUID
      );

      if (pollResultOptions) {
        const pollMessage = {
          id: POLL_MESSAGE_UUID,
          transcript: null,
          text: '',
          createdAt: addSeconds(
            new Date(sortedMessages[firstUserResponseIndex].createdAt),
            1
          ).toISOString(),
          sender: MessageSender.Assistant,
          type: project.questions?.[0]?.type,
          optionRenderType: OptionRenderType.Text,
          assets: null,
          options: pollResultOptions,
          maxSelections: null,
          contentUrl: null,
          totalResponses,
          questionId: project.questions?.[0]?.id ?? null,
          assistantQuestion: false,
          ...NO_MATRIX_FIELDS,
        };

        if (existingPollIndex !== -1) {
          // Update existing poll message
          sortedMessages[existingPollIndex] = pollMessage;
        } else {
          // Insert new poll message
          sortedMessages.splice(firstUserResponseIndex + 1, 0, pollMessage);
        }
      } else if (isPollResultLoading && existingPollIndex === -1) {
        // Only insert loading message if no poll message exists
        sortedMessages.splice(firstUserResponseIndex + 1, 0, {
          id: POLL_MESSAGE_LOADING_UUID,
          transcript: null,
          text: '',
          createdAt: addSeconds(
            new Date(sortedMessages[firstUserResponseIndex].createdAt),
            1
          ).toISOString(),
          sender: MessageSender.Assistant,
          type: project.questions?.[0]?.type,
          optionRenderType: OptionRenderType.Text,
          assets: null,
          options:
            project.questions?.[0]?.options?.map((o) => ({
              id: o.id,
              label: o.label,
              value: o.value,
              count: 0,
            })) ?? [],
          maxSelections: null,
          contentUrl: null,
          questionId: project.questions?.[0]?.id ?? null,
          assistantQuestion: false,
          ...NO_MATRIX_FIELDS,
        });
      }
    }

    const pollLoadingIdx = sortedMessages.findIndex(
      (m) => m.id === POLL_MESSAGE_LOADING_UUID
    );
    const shouldDeferFollowUp =
      responseMode !== 'voice' &&
      !project.isAiVoiceEnabled &&
      pollLoadingIdx !== -1;

    if (shouldDeferFollowUp) {
      const afterPoll = sortedMessages.slice(pollLoadingIdx + 1);
      const hasUserAfterPoll = afterPoll.some(
        (m) => m.sender === MessageSender.User
      );
      if (afterPoll.length > 0 && !hasUserAfterPoll) {
        const pollMessage = sortedMessages[pollLoadingIdx];
        sortedMessages = [
          ...sortedMessages.slice(0, pollLoadingIdx + 1),
          {
            id: OPTIMISTIC_MESSAGE_TEMP_REPLY_ID,
            text: '...',
            createdAt: addSeconds(
              new Date(pollMessage.createdAt),
              1
            ).toISOString(),
            sender: MessageSender.Assistant,
            type: undefined,
            options: [],
            optionRenderType: OptionRenderType.Text,
            assets: null,
            transcript: null,
            maxSelections: null,
            contentUrl: null,
            questionId: null,
            assistantQuestion: false,
            ...NO_MATRIX_FIELDS,
          },
        ];
      }
    }

    // Find the index of the first AI message with audio for autoPlay
    let firstAudioMessageIndex = -1;
    for (let i = 0; i < sortedMessages.length; i++) {
      const msg = sortedMessages[i];
      if (
        enumStringEquals(msg.sender, MessageSender.Assistant) &&
        speechAudios.some((s) => s.messageId === msg.id)
      ) {
        firstAudioMessageIndex = i;
        break;
      }
    }

    const getSpeechAudioData = (
      m: (typeof sortedMessages)[number],
      idx: number
    ) => {
      const speechAudio = speechAudios.find((s) => s.messageId === m.id);
      // AutoPlay only the first AI message with audio, queue handles the rest
      const autoPlay = idx === firstAudioMessageIndex;
      if (speechAudio) {
        return {
          ...speechAudio,
          autoPlay,
          messageId: m.id,
        };
      }
      return undefined;
    };

    return sortedMessages.map((m, idx) =>
      Object.assign({}, m, {
        audio:
          responseMode === 'voice' || project?.isAiVoiceEnabled
            ? getSpeechAudioData(m, idx)
            : undefined,
      })
    );
  })();

  return {
    messages,
  };
}
